import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import { accessService } from '../../../services/central-api/access';
import Toast from 'react-native-toast-message';
import MyAppText from '../../../components/MyAppText';
import { ClientError } from '../../../services/central-api/base-service';
import { Tabs } from '../../../components/Tabs';
import { ScrollView } from 'react-native-web-hover';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton from '../../../components/CustomButton';
import { NavigationProp, RouteProp, useFocusEffect, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { MyCheckbox } from '../../../components/MyCheckbox';
import OccurrenceTypeDropdown from '../../../components/access/OccurrenceTypeDropdown';
import { TextInputMask } from 'react-native-masked-text';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableContainer from '../../../components/DataTableContainer';
import MyDataTable from '../../../components/MyDataTable';
import { AccessParamList } from '../../../typings/Params';


export default function ActingBodiesForm() {
    const navigation = useNavigation<NavigationProp<AccessParamList>>();
    const route = useRoute<RouteProp<AccessParamList, 'ActingBodiesForm'>>();
    const actingBodyId = route.params?.id;
    const { styles, theme } = useStyles(styleSheet);


    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [needAttachmentToCloseOccurrence, setNeedAttachmentToCloseOccurrence] = useState(false);
    const [askForPoliceReport, setAskForPoliceReport] = useState(false);
    const [confidenceRequiredWantedPerson, setConfidenceRequiredWantedPerson] = useState(92);
    const [confidenceRequiredMissingPerson, setConfidenceRequiredMissingPerson] = useState(92);
    const [externalOccurrenceTypeMissingPerson, setExternalOccurrenceTypeMissingPerson] = useState<number | null>(null);
    const [externalOccurrenceTypeWantedPerson, setExternalOccurrenceTypeWantedPerson] = useState<number | null>(null);
    const [defaultLprOccurrenceTypeId, setDefaultLprOccurrenceTypeId] = useState<number | null>(null);
    const [deacMaxApplications, setDeacMaxApplications] = useState<number>(10);
    const [deacMaxUnjustifiedAbsences, setDeacMaxUnjustifiedAbsences] = useState<number>(2);
    const [deacRestPeriodBeforeWorkday, setDeacRestPeriodBeforeWorkday] = useState<number>(360);
    const [deacRestPeriodAfterWorkday, setDeacRestPeriodAfterWorkday] = useState<number>(360);
    const [inactivityDaysToBlockLogin, setInactivityDaysToBlockLogin] = useState<number>(0);


    // Read only props
    const [roles, setRoles] = useState<Role[]>([]);
    const [units, setUnits] = useState<ActingBodyUnit[]>([]);
    const [commands, setCommands] = useState<ActingBodyCommand[]>([]);


    function isEmpty(value: string) {
        return value == '';
    }


    function isConfidenceValueInvalid(value: number) {
        return value < 0 || value > 100;
    }


    function isFormValid() {
        return !isEmpty(name)
            && !isEmpty(description)
            && !isConfidenceValueInvalid(confidenceRequiredMissingPerson)
            && !isConfidenceValueInvalid(confidenceRequiredWantedPerson)
            && deacRestPeriodBeforeWorkday > 0
            && deacRestPeriodAfterWorkday > 0
            && inactivityDaysToBlockLogin >= 0;
    }


    function clearValues() {
        setName('');
        setDescription('');
        setNeedAttachmentToCloseOccurrence(false);
        setAskForPoliceReport(false);
        setRoles([]);
        setUnits([]);
        setCommands([]);
        setDeacMaxApplications(10);
        setDeacMaxUnjustifiedAbsences(2);
        setDeacRestPeriodBeforeWorkday(360);
        setDeacRestPeriodAfterWorkday(360);
        setInactivityDaysToBlockLogin(0);
    }


    async function getActingBody(actingBodyId?: number) {
        try {
            let actingBody;
            if (actingBodyId) {
                actingBody = await accessService.getActingBody(actingBodyId, { includeFacialInfo: true });
            }
            setName(actingBody?.name ?? '');
            setDescription(actingBody?.description ?? '');
            setNeedAttachmentToCloseOccurrence(actingBody?.needAttachmentToCloseOccurrence ?? false);
            setAskForPoliceReport(actingBody?.askForPoliceReport ?? false);
            setRoles(actingBody?.roles || []);
            setUnits(actingBody?.actingBodyUnits || []);
            setCommands(actingBody?.actingBodyCommands || []);
            setConfidenceRequiredMissingPerson(actingBody?.confidenceRequiredMissingPerson ?? 92);
            setConfidenceRequiredWantedPerson(actingBody?.confidenceRequiredWantedPerson ?? 92);
            setExternalOccurrenceTypeMissingPerson(actingBody?.externalOccurrenceTypeMissingPerson ?? null);
            setExternalOccurrenceTypeWantedPerson(actingBody?.externalOccurrenceTypeWantedPerson ?? null);
            setDefaultLprOccurrenceTypeId(actingBody?.defaultLprOccurrenceTypeId ?? null);
            setDeacMaxApplications(actingBody?.deacMaxApplications ?? 10);
            setDeacMaxUnjustifiedAbsences(actingBody?.deacMaxUnjustifiedAbsences ?? 2);
            setDeacRestPeriodBeforeWorkday(actingBody?.deacRestPeriodBeforeWorkday ?? 6);
            setDeacRestPeriodAfterWorkday(actingBody?.deacRestPeriodAfterWorkday ?? 6);
            setInactivityDaysToBlockLogin(actingBody?.inactivityDaysToBlockLogin ?? 0);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }


    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getActingBody(route.params?.id);
        }
    }, [isFocused, navigation, route.params]));


    async function formSubmit() {
        try {
            if (actingBodyId) {
                await accessService.updateActingBody({
                    id: actingBodyId,
                    name,
                    description,
                    needAttachmentToCloseOccurrence,
                    askForPoliceReport,
                    confidenceRequiredWantedPerson,
                    confidenceRequiredMissingPerson,
                    externalOccurrenceTypeMissingPerson,
                    externalOccurrenceTypeWantedPerson,
                    defaultLprOccurrenceTypeId,
                    deacMaxApplications,
                    deacMaxUnjustifiedAbsences,
                    deacRestPeriodAfterWorkday,
                    deacRestPeriodBeforeWorkday,
                    inactivityDaysToBlockLogin
                });
            } else {
                await accessService.createActingBody({
                    name,
                    description,
                    needAttachmentToCloseOccurrence,
                    askForPoliceReport,
                    confidenceRequiredWantedPerson,
                    confidenceRequiredMissingPerson,
                    deacMaxApplications,
                    deacMaxUnjustifiedAbsences,
                    deacRestPeriodAfterWorkday,
                    deacRestPeriodBeforeWorkday,
                    inactivityDaysToBlockLogin
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });


            clearValues();
            navigation.navigate('ActingBodies', {
                page: 1,
                limit: 25,
            });
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }


            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }


    return (
        <Container>
            <Header
                goBackAction={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('ActingBodies', {
                    page: 1,
                    limit: 25,
                })}
                title={translate('registerActingBodies')}
                actions={<CustomButton
                    text={translate('save')}
                    disabled={!isFormValid()}
                    onPress={formSubmit}
                />}
            />
            <Card>
                <Tabs tabs={[{
                    key: 'details',
                    label: translate('details')
                }, {
                    key: 'settings',
                    label: translate('settings')
                }, {
                    key: 'roles',
                    label: translate('roles'),
                    omit: !actingBodyId
                }, {
                    key: 'commands',
                    label: translate('commands'),
                    omit: !actingBodyId
                }, {
                    key: 'units',
                    label: translate('units'),
                    omit: !actingBodyId
                }]}>
                    {({ selectedTab }) => {
                        if (selectedTab == 'details') {
                            return (<View style={{ flex: 1, gap: 8, paddingHorizontal: 10 }}>
                                <FormInput
                                    label={translate('name')}
                                    placeholder={translate('name')}
                                    value={name}
                                    onChangeText={setName}
                                    invalid={isEmpty}
                                    required={true}
                                />
                                <FormInput
                                    label={translate('description')}
                                    placeholder={translate('description')}
                                    value={description}
                                    onChangeText={setDescription}
                                    invalid={isEmpty}
                                    required={true}
                                />
                            </View>);
                        } else if (selectedTab == 'settings') {
                            return (<ScrollView contentContainerStyle={styles.scrollableContainer}>
                                <View style={{ flex: 1, flexDirection: 'column', gap: 8, paddingHorizontal: 10, minWidth: 300 }}>
                                    <MyAppText style={styles.settingsSubTitle}>{translate('occurrences')}</MyAppText>
                                    <View style={{ flexDirection: 'column', rowGap: 4 }}>
                                        <MyCheckbox
                                            label={translate('needAttachmentToCloseOccurrence')}
                                            checked={needAttachmentToCloseOccurrence}
                                            setChecked={(value) => {
                                                setNeedAttachmentToCloseOccurrence(value);
                                            }}
                                        />
                                        <MyCheckbox
                                            label={translate('needPoliceReport')}
                                            checked={askForPoliceReport}
                                            setChecked={(value) => {
                                                setAskForPoliceReport(value);
                                            }}
                                        />
                                    </View>
                                    <MyAppText style={styles.settingsSubTitle}>{translate('facial')}</MyAppText>
                                    <View style={{ flexDirection: 'column', rowGap: 4, zIndex: 2 }}>
                                        <FormInput
                                            label={translate('confidenceRequiredWantedPerson')}
                                            required
                                            onChangeText={(value) => {
                                                setConfidenceRequiredWantedPerson(Number(value) || 0);
                                            }}
                                            value={confidenceRequiredWantedPerson.toString()}
                                            minValue={0}
                                            maxValue={100}
                                            mode='NUMBER'
                                            labelStyle={styles.fieldsLabelStyle}
                                            inputStyle={{ maxWidth: 120 }}
                                        />
                                        {actingBodyId ?
                                            <OccurrenceTypeDropdown
                                                actingBodyId={actingBodyId}
                                                setValue={(stateCallback) => {
                                                    const value = stateCallback(0);
                                                    if (value === 0) {
                                                        return setExternalOccurrenceTypeWantedPerson(null);
                                                    }
                                                    setExternalOccurrenceTypeWantedPerson(value);
                                                }}
                                                value={externalOccurrenceTypeWantedPerson || 0}
                                                allowEmpty={true}
                                                zIndex={99}
                                                labelStyle={styles.fieldsLabelStyle}
                                                label={translate('occurrenceTypeForWantedPeople')}
                                            /> : null
                                        }
                                        <FormInput
                                            label={translate('confidenceRequiredMissingPerson')}
                                            required
                                            onChangeText={(value) => {
                                                setConfidenceRequiredMissingPerson(Number(value) || 0);
                                            }}
                                            value={confidenceRequiredMissingPerson.toString()}
                                            minValue={0}
                                            maxValue={100}
                                            mode='NUMBER'
                                            labelStyle={styles.fieldsLabelStyle}
                                            inputStyle={{ maxWidth: 120 }}
                                        />
                                        {actingBodyId ?
                                            <OccurrenceTypeDropdown
                                                actingBodyId={actingBodyId}
                                                setValue={(stateCallback) => {
                                                    const value = stateCallback(0);
                                                    if (value === 0) {
                                                        return setExternalOccurrenceTypeMissingPerson(null);
                                                    }
                                                    setExternalOccurrenceTypeMissingPerson(value);
                                                }}
                                                value={externalOccurrenceTypeMissingPerson || 0}
                                                allowEmpty={true}
                                                zIndex={98}
                                                labelStyle={styles.fieldsLabelStyle}
                                                label={translate('occurrenceTypeForMissingPeople')}
                                            /> : null
                                        }
                                    </View>
                                    {actingBodyId && <>
                                        <MyAppText style={styles.settingsSubTitle}>{translate('lpr')}</MyAppText>
                                        <View style={{ flexDirection: 'column', rowGap: 4, zIndex: 1 }}>
                                            <OccurrenceTypeDropdown
                                                actingBodyId={actingBodyId}
                                                setValue={(stateCallback) => {
                                                    const value = stateCallback(0);
                                                    if (value === 0) {
                                                        return setDefaultLprOccurrenceTypeId(null);
                                                    }
                                                    setDefaultLprOccurrenceTypeId(value);
                                                }}
                                                value={defaultLprOccurrenceTypeId || 0}
                                                allowEmpty={true}
                                                zIndex={97}
                                                labelStyle={styles.fieldsLabelStyle}
                                                label={translate('occurrenceTypeForLpr')}
                                            />
                                        </View>
                                    </>}
                                </View>
                                <View style={{ flex: 1, flexDirection: 'column', gap: 20, minWidth: 300 }}>
                                    <View style={{ flexDirection: 'column', gap: 8, paddingHorizontal: 10, alignItems: 'flex-start' }}>
                                        <MyAppText style={styles.settingsSubTitle}>{translate('deac')}</MyAppText>
                                        <View style={{ flexDirection: 'column', rowGap: 4 }}>
                                            <FormInput
                                                label={translate('deacMaxApplicationsCount')}
                                                required
                                                onChangeText={(value) => {
                                                    setDeacMaxApplications(Number(value) || 0);
                                                }}
                                                value={deacMaxApplications.toString()}
                                                minValue={0}
                                                maxValue={100}
                                                mode='NUMBER'
                                                labelStyle={styles.fieldsLabelStyle}
                                            />
                                            <FormInput
                                                label={translate('deacMaxUnjustifiedAbsences')}
                                                required
                                                onChangeText={(value) => {
                                                    setDeacMaxUnjustifiedAbsences(Number(value) || 0);
                                                }}
                                                value={deacMaxUnjustifiedAbsences.toString()}
                                                minValue={0}
                                                maxValue={100}
                                                mode='NUMBER'
                                                labelStyle={styles.fieldsLabelStyle}
                                            />
                                            <>
                                                <View style={{ flexDirection: 'row', columnGap: 5 }}>
                                                    {<MyAppText style={styles.fieldsLabelStyle}>{translate('deacRestPeriodBeforeWorkday')}</MyAppText>}
                                                    <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                                                </View>
                                                <View style={[{ flexDirection: 'row' }]}>
                                                    <TextInputMask
                                                        value={String(deacRestPeriodBeforeWorkday)}
                                                        includeRawValueInChangeText={true}
                                                        onChangeText={(maskedText, rawText) => {
                                                            setDeacRestPeriodBeforeWorkday(Number(rawText));
                                                        }}
                                                        type={'only-numbers'}
                                                        style={[styles.inputAttached, { maxWidth: 70, textAlign: 'center' }]}
                                                    />
                                                    <MyAppText style={styles.inputAttach}>{translate('minutes')}</MyAppText>
                                                </View>
                                            </>


                                            <>
                                                <View style={{ flexDirection: 'row', columnGap: 5 }}>
                                                    {<MyAppText style={styles.fieldsLabelStyle}>{translate('deacRestPeriodAfterWorkday')}</MyAppText>}
                                                    <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                                                </View>
                                                <View style={[{ flexDirection: 'row' }]}>
                                                    <TextInputMask
                                                        value={String(deacRestPeriodAfterWorkday)}
                                                        includeRawValueInChangeText={true}
                                                        onChangeText={(maskedText, rawText) => {
                                                            setDeacRestPeriodAfterWorkday(Number(rawText));
                                                        }}
                                                        type={'only-numbers'}
                                                        style={[styles.inputAttached, { maxWidth: 70, textAlign: 'center' }]}
                                                    />
                                                    <MyAppText style={styles.inputAttach}>{translate('minutes')}</MyAppText>
                                                </View>
                                            </>
                                        </View>
                                    </View>
                                </View>
                                <View style={{ flex: 1, flexDirection: 'column', gap: 20, minWidth: 300 }}>
                                    <View style={{ flexDirection: 'column', gap: 8, paddingHorizontal: 10, alignItems: 'flex-start' }}>
                                        <MyAppText style={styles.settingsSubTitle}>{translate('accessConfiguration')}</MyAppText>
                                        <View style={{ flexDirection: 'column', rowGap: 4 }}>
                                            <>
                                                <View style={{ flexDirection: 'row', columnGap: 5 }}>
                                                    {<MyAppText style={styles.fieldsLabelStyle}>{translate('MaxInactivityPeriod')}</MyAppText>}
                                                    <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                                                </View>
                                                <View style={[{ flexDirection: 'row' }]}>
                                                    <TextInputMask
                                                        value={String(inactivityDaysToBlockLogin)}
                                                        includeRawValueInChangeText={true}
                                                        onChangeText={(maskedText, rawText) => {
                                                            setInactivityDaysToBlockLogin(Number(rawText));
                                                        }}
                                                        type={'only-numbers'}
                                                        style={[styles.inputAttached, { maxWidth: 70, textAlign: 'center' }]}
                                                    />
                                                    <MyAppText style={styles.inputAttach}>{translate('days')}</MyAppText>
                                                </View>
                                            </>
                                        </View>
                                    </View>
                                </View>


                            </ScrollView>);
                        } else if (selectedTab == 'roles') {
                            return <DataTableContainer
                                dataTable={<MyDataTable
                                    columns={[{
                                        name: translate('roleTitle'),
                                        selector: row => row.name,
                                        wrap: true,
                                        grow: 3
                                    }]}
                                    data={roles}
                                />}
                            />;
                        } else if (selectedTab == 'units') {
                            return <DataTableContainer
                                dataTable={<MyDataTable
                                    columns={[{
                                        name: translate('actingBodyUnitTitle'),
                                        selector: row => row.name,
                                        wrap: true,
                                        grow: 3
                                    }]}
                                    data={units}
                                />}
                            />;
                        } else if (selectedTab == 'commands') {
                            return <DataTableContainer
                                dataTable={<MyDataTable
                                    columns={[{
                                        name: translate('commandTitle'),
                                        selector: row => row.name,
                                        wrap: true,
                                        grow: 3
                                    }]}
                                    data={commands}
                                />}
                            />;
                        }
                    }}
                </Tabs>
            </Card>


        </Container>
    );
}


const styleSheet = createStyleSheet((theme) => ({
    scrollableContainer: {
        rowGap: 20,
        flexDirection: 'row',
        flex: 1,
        flexWrap: 'wrap'
    },
    settingsSubTitle: {
        color: theme.colors.primary['500'],
        fontWeight: 'bold',
        fontSize: 14,
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    inputAttached: {
        fontSize: 16,
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.container.background,
        maxHeight: 40,
        padding: 10,
        color: theme.colors.textColor,
        borderWidth: 1,
        borderColor: theme.colors.container.border,
        borderTopStartRadius: 4,
        borderBottomStartRadius: 4,
    },
    inputAttach: {
        color: theme.colors.textColor,
        fontSize: 13,
        backgroundColor: theme.colors.container.border,
        maxHeight: 40,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderRightWidth: 1,
        borderColor: theme.colors.container.border,
        borderTopEndRadius: 4,
        borderBottomEndRadius: 4,
        padding: 11,
    },
}));



